/**
 * Lab-panel "ask at order entry" questions (AOE).
 *
 * Provider panels and custom panels carry a `questions` array (empty on most),
 * and custom panels also carry each item's own questions under
 * `items[].questions`. A question is tied to a test through `marker_id`, but
 * the Quest catalog keys provider-panel questions by the *panel's* test code
 * (every item of the panel shares it), so they cannot be placed on one
 * biomarker. They are therefore shown once, for the panel.
 *
 * Shape, as the Quest catalog sends it:
 *   { id, code: "86006866|SMOKER", value: "CURRENT SMOKER",
 *     constraint: "ENTER Y, N OR NG (NOT GIVEN)", marker_id: "4714",
 *     required: true, sequence: 1, type: "text", answers: [], label,
 *     userAnswer }
 *
 * `label` is the backend's user-friendly wording of `value` (e.g. value
 * "SOURCE:" → label "What is the specimen source?") and is what we show.
 *
 * On an ordered panel every question is required, so `userAnswer` is set on
 * all of them once the order is placed; before that it is null.
 */
import {
  getApiErrorMessage,
  getApiValidationReasons,
} from "../../../common/utils/apiError";

export interface LabPanelQuestionAnswer {
  value?: string;
  label?: string;
  is_enabled?: boolean;
}

export interface LabPanelQuestion {
  id: string;
  code: string;
  /** The question itself, as the lab words it. */
  value: string;
  /** The user-friendly wording of the question; shown instead of `value`. */
  label?: string | null;
  /** Accepted answer format, e.g. "ENTER Y, N OR NG (NOT GIVEN)". */
  constraint?: string | null;
  /** The test `code` the question belongs to. */
  marker_id: string;
  required?: boolean;
  sequence?: number;
  type?: string;
  /** Choice questions list their options here; free-text ones have []. */
  answers?: LabPanelQuestionAnswer[] | null;
  /** The answer given when the panel was ordered; null until then. */
  userAnswer?: unknown;
}

/**
 * Snackbar copy for a failed prescription save that carried lab-panel
 * answers. A 422 on the lab's order-entry answers lists what is wrong per
 * question ("'WHEN VISITED (MM/DD/YYYY)' must be a date as MM/DD/YYYY");
 * those reasons are the message, under a short fixed headline, so the
 * affiliate knows which answer to fix. The backend's own `message` is not
 * used there: it repeats every reason in one sentence. Anything else is the
 * backend's message, or the given fallback.
 */
export const getAnswerSaveErrorSnackbar = (
  error: any,
  fallback: string
): { title: string; message: string } => {
  const validationReasons = getApiValidationReasons(error);
  if (validationReasons.length > 0) {
    return {
      title: "Some answers are not valid",
      message: validationReasons.join("\n"),
    };
  }
  return { title: "Error", message: getApiErrorMessage(error, fallback) };
};

const isQuestion = (question: any): question is LabPanelQuestion =>
  !!question && typeof question.value === "string";

/**
 * Every question of a panel, once each: the panel-level list plus any carried
 * only on an item (the same question may appear in both — deduplicated by
 * `id`, falling back to `code`).
 *
 * Ordered by the test they belong to, then by the lab's `sequence`, so the
 * questions of one biomarker sit together ("TRAVEL HISTORY" right before
 * "WHEN VISITED") instead of all the first questions, then all the second
 * ones. Tests come in the panel's own item order; a question whose
 * `marker_id` matches no item keeps its place of first appearance, after the
 * matched ones. A provider panel's questions all share one test code, so
 * there the sequence alone decides.
 */
export const getPanelQuestions = (panel: any): LabPanelQuestion[] => {
  const seen = new Set<string>();
  const out: LabPanelQuestion[] = [];
  const add = (list: unknown) => {
    if (!Array.isArray(list)) return;
    list.forEach((question) => {
      if (!isQuestion(question)) return;
      const key = String(question.id ?? question.code ?? question.value);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(question);
    });
  };
  add(panel?.questions);
  const items: any[] = Array.isArray(panel?.items) ? panel.items : [];
  items.forEach((item) => add(item?.questions));

  // Rank of each test code: the panel's items first, in their order, then any
  // code only the questions know, in order of first appearance.
  const rank = new Map<string, number>();
  items.forEach((item) => {
    const code = String(item?.code ?? "");
    if (code && !rank.has(code)) rank.set(code, rank.size);
  });
  out.forEach((question) => {
    const code = String(question.marker_id ?? "");
    if (!rank.has(code)) rank.set(code, rank.size);
  });

  return out
    .map((question, index) => ({ question, index }))
    .sort((a, b) => {
      const byTest =
        rank.get(String(a.question.marker_id ?? ""))! -
        rank.get(String(b.question.marker_id ?? ""))!;
      if (byTest !== 0) return byTest;
      const bySequence =
        (a.question.sequence ?? 0) - (b.question.sequence ?? 0);
      return bySequence !== 0 ? bySequence : a.index - b.index;
    })
    .map(({ question }) => question);
};

/**
 * The wording to show for a question: the backend's friendly `label`, or the
 * lab's own `value` when there is no label (missing or blank).
 */
export const getQuestionText = (question: LabPanelQuestion): string =>
  (typeof question.label === "string" && question.label.trim()) ||
  question.value;

/**
 * The answer to show for a question: the chosen option's label when the
 * question has options and `userAnswer` matches one, otherwise the raw
 * answer as text. null when it has not been answered.
 */
export const getQuestionAnswerLabel = (
  question: LabPanelQuestion
): string | null => {
  const raw = question.userAnswer;
  if (raw === null || raw === undefined || raw === "") return null;
  const match = Array.isArray(question.answers)
    ? question.answers.find((answer) => String(answer?.value) === String(raw))
    : undefined;
  return match?.label || String(raw);
};

/** True once any question carries an answer — i.e. the panel was ordered. */
export const hasAnsweredQuestions = (questions: LabPanelQuestion[]): boolean =>
  questions.some((question) => getQuestionAnswerLabel(question) !== null);

/**
 * A question still waiting for its answer. Every question the lab asks is
 * required unless it says otherwise (`required: false`), and a blank answer
 * counts as none — the lab rejects the order over it just the same.
 */
export const isQuestionUnanswered = (question: LabPanelQuestion): boolean =>
  question.required !== false &&
  (question.userAnswer === null ||
    question.userAnswer === undefined ||
    String(question.userAnswer).trim() === "");

/**
 * The panel's questions that still need an answer, in the lab's order. Empty
 * for a panel without questions, or once they were all answered.
 */
export const getUnansweredQuestions = (panel: any): LabPanelQuestion[] =>
  getPanelQuestions(panel).filter(isQuestionUnanswered);

/** True while at least one of the panel's questions has no answer. */
export const hasUnansweredQuestions = (panel: any): boolean =>
  getUnansweredQuestions(panel).length > 0;

/**
 * The biomarkers of the panel a question refers to, by `marker_id` = item
 * `code` — but only when that singles some of them out. A provider panel's
 * items all share the panel code, so every question matches every item and
 * naming them would say nothing; that case (and no match at all) returns [].
 */
export const getQuestionBiomarkerNames = (
  panel: any,
  question: LabPanelQuestion
): string[] => {
  const items: any[] = Array.isArray(panel?.items) ? panel.items : [];
  if (items.length === 0) return [];
  const matching = items.filter(
    (item) => String(item?.code) === String(question.marker_id)
  );
  if (matching.length === 0 || matching.length === items.length) return [];
  return matching.map((item) => String(item.name || item.biomarkerid || ""));
};
